// ============================================================================
//  TUTORIAL SERVICE - Multi-Page Tutorial System

// Tracks tutorial progress across different pages/features
// ============================================================================

import { doc, getDoc, updateDoc, Timestamp, setDoc } from 'firebase/firestore';
import { db } from './firebase';

// ============================================================================
// TUTORIAL TYPES
// ============================================================================

export type TutorialType = 
  | 'main'        // Main app tour (HomePage)
  | 'wallet'      // Wallet/withdraw tutorial
  | 'tables'      // Finding/joining tables
  | 'create'      // Creating games
  | 'agent'       // Agent dashboard (agents only)
  | 'social';     // Friends/messaging

export interface TutorialProgress {
  completed: boolean;
  skipped: boolean;
  completedAt?: Date;
  currentStep?: number;
}

export interface UserTutorials {
  main: TutorialProgress;
  wallet: TutorialProgress;
  tables: TutorialProgress;
  create: TutorialProgress;
  agent: TutorialProgress;
  social: TutorialProgress;
  version: string;
}

// Current version - increment to re-show tutorials
const TUTORIAL_VERSION = '1.1.0'; // Updated: Added training rewards steps

// Default progress
const DEFAULT_PROGRESS: TutorialProgress = {
  completed: false,
  skipped: false,
  currentStep: 0,
};

// ============================================================================
// TUTORIAL SERVICE CLASS
// ============================================================================

class TutorialService {
  
  // ==========================================================================
  // GET USER'S TUTORIAL DATA
  // ==========================================================================
  
  async getUserTutorials(userId: string): Promise<UserTutorials> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return this.getDefaultTutorials();
      }
      
      const data = userDoc.data();
      const tutorials = data.tutorials as UserTutorials | undefined;
      
      // If no tutorials or version changed, return defaults
      if (!tutorials || tutorials.version !== TUTORIAL_VERSION) {
        return this.getDefaultTutorials();
      }
      
      return tutorials;
    } catch (error) {
      console.error('❌ [Tutorial] Error getting tutorials:', error);
      return this.getDefaultTutorials();
    }
  }
  
  private getDefaultTutorials(): UserTutorials {
    return {
      main: { ...DEFAULT_PROGRESS },
      wallet: { ...DEFAULT_PROGRESS },
      tables: { ...DEFAULT_PROGRESS },
      create: { ...DEFAULT_PROGRESS },
      agent: { ...DEFAULT_PROGRESS },
      social: { ...DEFAULT_PROGRESS },
      version: TUTORIAL_VERSION,
    };
  }
  
  // ==========================================================================
  // CHECK IF TUTORIAL SHOULD SHOW
  // ==========================================================================
  
  async shouldShowTutorial(userId: string, type: TutorialType): Promise<boolean> {
    try {
      const tutorials = await this.getUserTutorials(userId);
      const progress = tutorials[type];
      return !progress.completed && !progress.skipped;
    } catch (error) {
      console.error('❌ [Tutorial] Error checking tutorial:', error);
      return false;
    }
  }
  
  // Alias for backward compatibility
  async hasCompletedTutorial(userId: string, type: TutorialType = 'main'): Promise<boolean> {
    try {
      const tutorials = await this.getUserTutorials(userId);
      return tutorials[type]?.completed || false;
    } catch (error) {
      return false;
    }
  }
  
  // ==========================================================================
  // COMPLETE TUTORIAL
  // ==========================================================================
  
  async completeTutorial(userId: string, type: TutorialType = 'main'): Promise<void> {
    try {
      console.log(`📚 [Tutorial] Completing ${type} tutorial for:`, userId.substring(0, 8));
      
      const tutorials = await this.getUserTutorials(userId);
      tutorials[type] = {
        completed: true,
        skipped: false,
        completedAt: new Date(),
        currentStep: 999,
      };
      tutorials.version = TUTORIAL_VERSION;
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        tutorials,
        [`tutorial_${type}_completed`]: true,
        [`tutorial_${type}_completedAt`]: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      
      console.log(`✅ [Tutorial] ${type} tutorial completed`);
    } catch (error) {
      console.error('❌ [Tutorial] Error completing tutorial:', error);
    }
  }
  
  // ==========================================================================
  // SKIP TUTORIAL
  // ==========================================================================
  
  async skipTutorial(userId: string, type: TutorialType = 'main'): Promise<void> {
    try {
      console.log(`📚 [Tutorial] Skipping ${type} tutorial for:`, userId.substring(0, 8));
      
      const tutorials = await this.getUserTutorials(userId);
      tutorials[type] = {
        completed: true,
        skipped: true,
        currentStep: 0,
      };
      tutorials.version = TUTORIAL_VERSION;
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        tutorials,
        [`tutorial_${type}_skipped`]: true,
        updatedAt: Timestamp.now(),
      });
      
      console.log(`✅ [Tutorial] ${type} tutorial skipped`);
    } catch (error) {
      console.error('❌ [Tutorial] Error skipping tutorial:', error);
    }
  }
  
  // ==========================================================================
  // SAVE PROGRESS
  // ==========================================================================
  
  async saveTutorialProgress(userId: string, step: number, type: TutorialType = 'main'): Promise<void> {
    try {
      if (!userId) return;
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        [`tutorial_${type}_step`]: step,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      // Non-critical, don't throw
      console.warn('⚠️ [Tutorial] Could not save progress');
    }
  }
  
  // ==========================================================================
  // RESET TUTORIAL
  // ==========================================================================
  
  async resetTutorial(userId: string, type: TutorialType = 'main'): Promise<void> {
    try {
      console.log(`📚 [Tutorial] Resetting ${type} tutorial for:`, userId.substring(0, 8));
      
      const tutorials = await this.getUserTutorials(userId);
      tutorials[type] = { ...DEFAULT_PROGRESS };
      tutorials.version = TUTORIAL_VERSION;
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        tutorials,
        [`tutorial_${type}_completed`]: false,
        [`tutorial_${type}_skipped`]: false,
        [`tutorial_${type}_step`]: 0,
        updatedAt: Timestamp.now(),
      });
      
      console.log(`✅ [Tutorial] ${type} tutorial reset`);
    } catch (error) {
      console.error('❌ [Tutorial] Error resetting tutorial:', error);
      throw error;
    }
  }
  
  // ==========================================================================
  // RESET ALL TUTORIALS
  // ==========================================================================
  
  async resetAllTutorials(userId: string): Promise<void> {
    try {
      console.log(`📚 [Tutorial] Resetting ALL tutorials for:`, userId.substring(0, 8));
      
      const defaultTutorials = this.getDefaultTutorials();
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        tutorials: defaultTutorials,
        tutorial_main_completed: false,
        tutorial_wallet_completed: false,
        tutorial_tables_completed: false,
        tutorial_create_completed: false,
        tutorial_agent_completed: false,
        tutorial_social_completed: false,
        updatedAt: Timestamp.now(),
      });
      
      console.log(`✅ [Tutorial] All tutorials reset`);
    } catch (error) {
      console.error('❌ [Tutorial] Error resetting all tutorials:', error);
      throw error;
    }
  }
}

// Export singleton
export const tutorialService = new TutorialService();
export default tutorialService;